import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/layout/Navigation';
import { Input, Select } from '../components/ui';
import { SchoolCard } from '../components/SchoolCard';

interface School {
  id: string;
  name: string;
  location: string;
  division: string;
  conference: string;
  type: 'Men' | 'Women' | 'Both';
  enrollment: number;
  tuition: number;
  acceptanceRate: number;
  size: string;
  founded: string;
}

// Mock data for schools
const mockSchools: School[] = [
  {
    id: '1',
    name: 'Harvard University',
    location: 'Cambridge, MA',
    division: 'Division I',
    conference: 'Ivy League',
    type: 'Both',
    enrollment: 7200,
    tuition: 54000,
    acceptanceRate: 4.6,
    size: 'Medium',
    founded: '1636',
  },
  {
    id: '2',
    name: 'Yale University',
    location: 'New Haven, CT',
    division: 'Division I',
    conference: 'Ivy League',
    type: 'Both',
    enrollment: 6500,
    tuition: 59000,
    acceptanceRate: 5.9,
    size: 'Medium',
    founded: '1701',
  },
  {
    id: '3',
    name: 'University of Washington',
    location: 'Seattle, WA',
    division: 'Division I',
    conference: 'Pac-12',
    type: 'Both',
    enrollment: 48000,
    tuition: 39000,
    acceptanceRate: 48.7,
    size: 'Large',
    founded: '1861',
  },
  {
    id: '4',
    name: 'Williams College',
    location: 'Williamstown, MA',
    division: 'Division III',
    conference: 'NESCAC',
    type: 'Both',
    enrollment: 2200,
    tuition: 61000,
    acceptanceRate: 12.6,
    size: 'Small',
    founded: '1793',
  },
  {
    id: '5',
    name: 'UC Berkeley',
    location: 'Berkeley, CA',
    division: 'Division I',
    conference: 'Pac-12',
    type: 'Both',
    enrollment: 45000,
    tuition: 44000,
    acceptanceRate: 14.5,
    size: 'Large',
    founded: '1868',
  },
];

const SchoolsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    division: 'all',
    conference: 'all',
    type: 'all',
  });

  const filteredSchools = mockSchools.filter((school) => {
    const matchesSearch =
      school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDivision = filters.division === 'all' || school.division === filters.division;
    const matchesConference =
      filters.conference === 'all' || school.conference === filters.conference;
    const matchesType = filters.type === 'all' || school.type === filters.type;

    return matchesSearch && matchesDivision && matchesConference && matchesType;
  });

  return (
    <div className="min-h-screen bg-rowing-gray-50">
      <Navigation isAuthenticated={true} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-display font-bold text-rowing-navy mb-8">
            Browse Rowing Programs
          </h1>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-4 lg:col-span-1">
                <Input
                  label="Search"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="School name or location..."
                />
              </div>

              <div>
                <Select
                  label="Division"
                  value={filters.division}
                  onChange={(e) => setFilters({ ...filters, division: e.target.value })}
                  options={[
                    { value: 'all', label: 'All Divisions' },
                    { value: 'Division I', label: 'Division I' },
                    { value: 'Division II', label: 'Division II' },
                    { value: 'Division III', label: 'Division III' },
                  ]}
                />
              </div>

              <div>
                <Select
                  label="Conference"
                  value={filters.conference}
                  onChange={(e) => setFilters({ ...filters, conference: e.target.value })}
                  options={[
                    { value: 'all', label: 'All Conferences' },
                    { value: 'Ivy League', label: 'Ivy League' },
                    { value: 'Pac-12', label: 'Pac-12' },
                    { value: 'NESCAC', label: 'NESCAC' },
                    { value: 'ACC', label: 'ACC' },
                    { value: 'Big Ten', label: 'Big Ten' },
                  ]}
                />
              </div>

              <div>
                <Select
                  label="Program Type"
                  value={filters.type}
                  onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                  options={[
                    { value: 'all', label: 'All Programs' },
                    { value: 'Men', label: "Men's Only" },
                    { value: 'Women', label: "Women's Only" },
                    { value: 'Both', label: 'Both' },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Results Count */}
          <p className="text-rowing-gray-600 mb-4">
            Showing {filteredSchools.length} of {mockSchools.length} programs
          </p>

          {/* Schools Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSchools.map((school, index) => (
              <motion.div
                key={school.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <SchoolCard school={school} />
              </motion.div>
            ))}
          </div>

          {filteredSchools.length === 0 && (
            <div className="text-center py-12">
              <p className="text-rowing-gray-600">No schools match your search criteria.</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default SchoolsPage;
